//================================================================
//  Component: Add Subnet
//================================================================

//  Purpose: Add a subnet to the sites collection

//  Properties:
//    - None

//  AddSubnet:
//    <AddSubnet></AddSubnet>    

//================================================================


//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Netmask } from 'netmask';

//Contexts
import { GetUser, SetToast } from '../../../../Library/GlobalContexts';

//Functions
import WriteDocument from '../../../../Library/WriteDocument';
import QueryDocument from '../../../../Library/QueryDocument';

// Images
import SuccessIcon from '../../../../Components/Images/Icon_Save_Teal.svg';
import IconAdd from '../../../../Components/Images/Icon_Add_White.svg';


export default function AddSubnet() {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Open or Close the 'Add Subnet' side pane
    const [addSubnet, setAddSubnet] = useState(false);

    // Raw Firestore data
    const [allRegions, setAllRegions] = useState([]);

    // Filtered Firestore data for select boxes (Q1 & Q2)
    const [regionsUnique, setRegionsUnique] = useState([]);
    const [supernets, setSupernets] = useState([]);

    // Selected supernet (Q2), used in validation
    const [selectedSupernet, setSelectedSupernet] = useState();

    // Duplication check -> 'onload', 'pending', 'failed' & 'complete'
    const [duplicateSubnet, setDuplicateSubnet] = useState('onload');

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    // https://react-hook-form.com/get-started
    const {
        register,
        handleSubmit,
        reset,
        getValues,
        setValue,
        setError,
        clearErrors,
        formState: { isSubmitSuccessful, errors },
    } = useForm();

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    function validateSubnet(evt) {

        try {

            evt.preventDefault();

            // Clear errors and values from previous validation
            clearErrors('subnetip');

            const siteIp = getValues('subnetip');

            // Check for a valid CIDR before validating...
            // https://blog.markhatton.co.uk/2011/03/15/regular-expressions-for-ip-addresses-cidr-ranges-and-hostnames/
            if (/^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/(3[0-2]|[1-2][0-9]|[0-9]))$/.test(siteIp) === false) {

                setError('subnetip', {
                    message: `Invalid CIDR provided! Expected format, 'XXX.XXX.XXX.XXX/XX'`
                });

                setDuplicateSubnet('onload');

                return;

            }

            // Proceed with API calls
            setDuplicateSubnet('pending');

            //------------------------------------------------------
            //  Get subnet information
            //------------------------------------------------------

            // https://www.npmjs.com/package/netmask
            // Benno 14-04-2025 ~ Discussed with Jeffey & Alex, they are comfortable with an external dependency.
            const newSubnet = new Netmask(siteIp);

            // Save to form
            setValue('subnetmask', newSubnet.mask);
            setValue('broadcastaddress', newSubnet.broadcast);
            setValue('assignablehosts', newSubnet.size - 2); // Remove broadcast IP's
            setValue('firstassignablehost', newSubnet.first);
            setValue('lastassignablehost', newSubnet.last);

            //------------------------------------------------------
            //  Does the subnet fall WITHIN the region/supernet?
            //------------------------------------------------------

            const currentSubnet = new Netmask(selectedSupernet.supernet);

            if (currentSubnet.contains(siteIp) === false) {

                setError('subnetip', {
                    message: `'${siteIp}' falls outside of the selected supernet of '${selectedSupernet.supernet}'`
                });
                setValue('subnetmask', '');
                setValue('broadcastaddress', '');
                setValue('assignablehosts', '');
                setValue('firstassignablehost', '');
                setValue('lastassignablehost', '');
                setDuplicateSubnet('failed');

                return;

            }

            //------------------------------------------------------
            //  Not allowed to be a BASE IP address e.g. 10.0.0.0
            //------------------------------------------------------

            if (newSubnet.base !== siteIp.split('/')[0]) {

                setError('subnetip', {
                    message: `Provide a valid network address. Expecting a base network address.`
                });
                setValue('subnetmask', '');
                setValue('broadcastaddress', '');
                setValue('assignablehosts', '');
                setValue('firstassignablehost', '');
                setValue('lastassignablehost', '');
                setDuplicateSubnet('failed');

                return;

            }

            //------------------------------------------------------
            //  Check Firestore for duplicates WITHIN the current region!
            //------------------------------------------------------

            QueryDocument('subnets', [
                ['supernet', '==', selectedSupernet.supernet],
                ['type', '==', 'subnet'],
            ]).then((subnets) => {

                const cidrNotations = [];

                // Check each subnet for a conflict
                subnets.forEach((subnet) => {

                    const currentSubnet = new Netmask(subnet.subnetip);

                    if (currentSubnet.contains(siteIp) === true) {

                        // Holds duplicates
                        cidrNotations.push(
                            subnet.subnetip
                        );

                    }

                    const newSubnet = new Netmask(siteIp);

                    if (newSubnet.contains(subnet.subnetip) === true) {

                        // Prevent duplicates
                        if (cidrNotations.includes(subnet.subnetip)) return;

                        // Holds duplicates
                        cidrNotations.push(
                            subnet.subnetip
                        );

                    }

                });

                if (cidrNotations.length > 0) {

                    setError('subnetip', {
                        message: `Found an existing subnet(s): ${cidrNotations}`
                    });
                    setValue('subnetmask', '');
                    setValue('broadcastaddress', '');
                    setValue('assignablehosts', '');
                    setValue('firstassignablehost', '');
                    setValue('lastassignablehost', '');
                    setDuplicateSubnet('failed');

                    return;

                }

                // No duplicates
                setDuplicateSubnet('complete');
                clearErrors('subnetip');

            });


        } catch (error) {

            console.log(error);
            setDuplicateSubnet('failed');
            clearErrors('subnetip');

            // Set error on toast
            setToast({
                'type': 'error',
                'message': 'Failed to complete validation request. If the error persists contact support.',
            });
        }

    }

    // Submit Handler
    const onSubmit = (formData) => {

        //------------------------------------------------------
        //  Final duplicate check
        //------------------------------------------------------

        QueryDocument('subnets', [
            ['supernet', '==', formData.supernet],
            ['type', '==', 'subnet'],
        ]).then((subnets) => {

            const cidrNotations = [];

            // Check each subnet for a conflict
            subnets.forEach((subnet) => {

                const currentSubnet = new Netmask(subnet.subnetip);

                if (currentSubnet.contains(formData.subnetip) === true) {

                    cidrNotations.push(
                        subnet.subnetip
                    );

                }

            });

            if (cidrNotations.length > 0) {

                setError('subnetip', {
                    message: `Found an existing subnet(s): ${cidrNotations}`
                });

                // Form resets on submission, add back in allowed values
                setValue('region', formData.region);
                setValue('supernet', formData.supernet);
                setValue('subnetip', formData.subnetip);
                setValue('siteid', formData.siteid);
                setValue('description', formData.description);
                setValue('vlan', formData.vlan);
                setValue('additionalinfo', formData.additionalinfo);

                return;

            }

            // No duplicates
            setDuplicateSubnet('complete');
            clearErrors('subnetip');

            //------------------------------------------------------
            //  Proceed with submission
            //------------------------------------------------------

            // Open the toast
            setToast({
                'type': 'pending',
                'message': 'Creating subnet please wait...',
            });

            const documentid = formData.subnetip.replace('/', '-');

            const documentObject = {
                'subnetid': documentid,
                'type': 'subnet',
                'region': formData.region,
                'supernet': formData.supernet,
                'supernetid': selectedSupernet.supernetid,
                'subnetip': formData.subnetip,
                'description': formData.description.trim(),
                'vlan': formData.vlan,
                'subnetmask': formData.subnetmask,
                'assignablehosts': formData.assignablehosts,
                'hostmin': formData.firstassignablehost,
                'hostmax': formData.lastassignablehost,
                'createddate': new Date(),
                'createdby': {
                    'email': getUser.emailaddress,
                    'givenname': getUser.givenname,
                    'surname': getUser.surname,
                },
                'siteid': formData.siteid,
                'lastmodifieddate': new Date(),
                'lastmodifiedby': {
                    'email': getUser.emailaddress,
                    'givenname': getUser.givenname,
                    'surname': getUser.surname,
                },
                'additionalinfo': formData.additionalinfo.trim(),
                'status': 'active',
            };

            return WriteDocument('subnets', documentid, documentObject, false).then(() => {

                // Close Side Pane
                setAddSubnet(false);

                // Show Success Toast
                setToast({
                    'type': 'success',
                    'message':
                        <>
                            Subnet added. <a href={process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ? `https://devinventory.digitalservices.lendlease.com/inventory/subnet/${documentid}?view=overview` : process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ? `https://uatinventory.digitalservices.lendlease.com/inventory/subnet/${documentid}?view=overview` : `https://inventory.digitalservices.lendlease.com/inventory/subnet/${documentid}?view=overview`} target='_blank' rel='noopener noreferrer'>{documentObject.subnetip}</a>.
                        </>
                });

            });

        }).catch((error) => {

            console.log('error', error);

            // Set error on toast
            setToast({
                'type': 'error',
                'message': 'Failed to create subnet, refresh the page and try again.',
            });

        });

    };

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Reset form onclose
    useEffect(() => {

        if (isSubmitSuccessful) {
            reset();
            setDuplicateSubnet('onload');
        };

    }, [isSubmitSuccessful, reset]);

    // Fetch all 'Regions'
    //  Important - Regions can ONLY be created by admins and duplicates are ALLOWED
    useEffect(() => {

        if (addSubnet === false) return;

        QueryDocument('subnets', [
            ['type', '==', 'region'],
        ]).then((regionDocs) => {

            // Save the raww Firestore data
            setAllRegions(regionDocs);

            // Get each 'unique' region ~ https://www.geeksforgeeks.org/how-to-get-distinct-values-from-an-array-of-objects-in-javascript/
            // Yes we are required to allow duplicates in Regions :-(
            const results = regionDocs.filter((obj, index) =>
                index === regionDocs.findIndex((value) => value.region === obj.region)
            );
            setRegionsUnique(results);

        }).catch((error) => {

            console.log('error', error);

            // Set error on toast
            setToast({
                'type': 'error',
                'message': 'Failed to fetch regions, refresh the page and try again.',
            });

        });

        // eslint-disable-next-line
    }, [addSubnet]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    if (addSubnet === false) return (

        <button className='Primary-Button whitespace-nowrap flex flex-row gap-2' onClick={() => setAddSubnet(true)}>
            <img src={IconAdd} alt='Add'></img> Add Subnet
        </button>

    );

    return (
        <>

            {/* ======== Keeps the button is visible during slide animation ========= */}
            <button className='Primary-Button whitespace-nowrap flex flex-row gap-2' onClick={() => setAddSubnet(true)}>
                <img src={IconAdd} alt='Add'></img> Add Subnet
            </button>

            {/* =========================================================== */}
            {/*  Add Subnet Pane                                            */}
            {/* =========================================================== */}

            <div className='Pane-Background'>

                <dialog className='Pane-Container'>

                    {/*  ========= Add Subnet Form * ========= */}
                    <form className='grid grid-rows-[70px_1fr_70px] items-center justify-between w-full overflow-hidden' onSubmit={handleSubmit(onSubmit)}>

                        {/* Header */}
                        <div className='flex flex-col gap-2 p-4'>
                            <h4 className='text-[22px] p-0 m-0'> Add Subnet </h4>
                        </div>

                        <div className='w-[50vw] h-full flex flex-col overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

                            {/*  ========= 1. Region * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>1. Region <span className='text-[#C4314B]'>*</span></label>
                                <i className='font-light'>Creation of regions is restricted to administrators</i>

                                <select
                                    className={errors.region ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                                    {...register('region', {
                                        required: 'This field is required'
                                    })}
                                    style={{ width: '300px' }}
                                    onChange={(e) => {

                                        // Reset child fields
                                        setValue('supernet', '');
                                        setValue('subnetip', '');
                                        clearErrors('subnetip');
                                        setDuplicateSubnet('onload');

                                        // Save values
                                        const value = e.target.value;

                                        setValue('region', value);

                                        // Find all supernets with this region!
                                        const results = allRegions.filter((object) => object?.region === value);

                                        setSupernets(results);

                                    }}
                                >
                                    <option hidden value=''>-</option>
                                    {
                                        regionsUnique.map((object) => (
                                            <option value={object?.region}>{object?.region}</option>
                                        ))
                                    }
                                </select>

                                {errors.region && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.region?.message}</p>}
                            </div>

                            {/*  ========= 2. Supernet * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>2. Supernet <span className='text-[#C4314B]'>*</span></label>
                                <i className='font-light'>Creation of supernets is restricted to administrators</i>

                                <select
                                    className={errors.supernet ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                                    {...register('supernet', {
                                        required: 'This field is required'
                                    })}
                                    style={{ width: '300px' }}
                                    disabled={regionsUnique.length === 0 ? true : false}
                                    onChange={(e) => {

                                        // Save values
                                        const value = e.target.value;
                                        setValue('supernet', value);

                                        // Used by the subnet validation
                                        setSelectedSupernet(
                                            supernets.filter((object) => object.supernet === value)[0] // should ONLY be one!
                                        );

                                    }}
                                >
                                    <option hidden value=''>-</option>
                                    {
                                        supernets.map((object) => (
                                            <option value={object?.supernet}>{object?.supernet}</option>
                                        ))
                                    }
                                </select>

                                {errors.supernet && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.supernet?.message}</p>}
                            </div>

                            {/*  ========= 3. Subnet * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>3. Subnet <span className='text-[#C4314B]'>*</span></label>

                                <div className='flex flex-row gap-2 items-center'>

                                    <input
                                        className={errors.subnetip ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                        type='text'
                                        placeholder='172.19.0.0/24'
                                        autoComplete='no'
                                        {...register(
                                            'subnetip', {
                                            required: 'This is a required field.',
                                            maxLength: 50,
                                        })}
                                        disabled={duplicateSubnet === 'pending' ? true : false}
                                        onChange={() => setDuplicateSubnet('onload')}
                                    ></input>

                                    {/* Validate Subnet Button */}
                                    <div className='flex flex-row gap-2'>
                                        <button disabled={(duplicateSubnet === 'complete')} className='Primary-Button' onClick={(e) => validateSubnet(e)}>Validate</button>
                                        {
                                            duplicateSubnet === 'complete' &&
                                                <img className='LiveField-Success' src={SuccessIcon} alt='icon-success'></img>
                                        }
                                    </div>

                                </div>

                                {errors.subnetip && <p className='font-medium text-sm text-[var(--darkred)] my-2 overflow-hidden text-ellipsis'>{errors.subnetip?.message}</p>}
                            </div>

                            {/*  ========= 4. Site ID  ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>4. Site ID <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.siteid ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='text'
                                    placeholder='SYD-100'
                                    autoComplete='no'
                                    {...register(
                                        'siteid', {
                                        required: 'This is a required field.',
                                    })}
                                ></input>
                                {errors.siteid && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.siteid?.message}</p>}
                            </div>

                            {/*  ========= 5. Vlan ID * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>5. Vlan ID <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.vlan ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='text'
                                    placeholder='80'
                                    autoComplete='no'
                                    disabled={duplicateSubnet === 'complete' ? false : true}
                                    {...register(
                                        'vlan', {
                                        required: 'This is a required field.',
                                    })}
                                ></input>
                                {errors.vlan && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.vlan?.message}</p>}
                            </div>

                            {/*  ========= 6. Description * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>6. Description <span className='text-[#C4314B]'>*</span></label>
                                <textarea
                                    className={errors.description ? 'Input-Field-TextArea-Error' : 'Input-Field-TextArea'}
                                    style={{ height: '110px' }}
                                    type='text'
                                    placeholder='Add a description…'
                                    {...register('description', {
                                        required: 'This is a required field.',
                                        maxLength: 200,
                                    })}
                                    disabled={duplicateSubnet === 'complete' ? false : true}
                                ></textarea>

                                {errors.description && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.description?.message}</p>}
                            </div>

                            {/*  ========= 7. Netmask  ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>7. Netmask <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.subnetmask ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='text'
                                    placeholder='255.255.255.0'
                                    autoComplete='no'
                                    disabled={true}
                                    {...register(
                                        'subnetmask', {
                                    })}
                                ></input>
                                {errors.subnetmask && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.subnetmask?.message}</p>}
                            </div>

                            {/*  ========= 8. Hosts/Net * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>8. Hosts/Net <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.assignablehosts ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='number'
                                    placeholder='254'
                                    autoComplete='no'
                                    disabled={true}
                                    {...register(
                                        'assignablehosts', {
                                        required: 'This is a required field.',
                                    })}
                                ></input>
                                {errors.assignablehosts && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.assignablehosts?.message}</p>}
                            </div>

                            {/*  ========= 9. HostMin  ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>9. HostMin <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.firstassignablehost ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='text'
                                    placeholder='172.19.0.1'
                                    autoComplete='no'
                                    disabled={true}
                                    {...register(
                                        'firstassignablehost', {
                                        required: 'This is a required field.',
                                    })}
                                ></input>
                                {errors.firstassignablehost && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.firstassignablehost?.message}</p>}
                            </div>

                            {/*  ========= 10. HostMax * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>10. HostMax <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.lastassignablehost ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='text'
                                    placeholder='172.19.0.254'
                                    autoComplete='no'
                                    disabled={true}
                                    {...register(
                                        'lastassignablehost', {
                                        required: 'This is a required field.',
                                    })}
                                ></input>
                                {errors.lastassignablehost && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.lastassignablehost?.message}</p>}
                            </div>

                            {/*  ========= 11. Additional Info * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>11. Additional Info <span className='text-[#C4314B]'>*</span></label>
                                <textarea
                                    className={errors.additionalinfo ? 'Input-Field-TextArea-Error' : 'Input-Field-TextArea'}
                                    style={{ height: '110px' }}
                                    type='text'
                                    placeholder='VRF, VPC, Zone…'
                                    {...register('additionalinfo', {
                                        required: 'This is a required field.',
                                        maxLength: 200,
                                    })}
                                ></textarea>

                                {errors.additionalinfo && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.additionalinfo?.message}</p>}
                            </div>

                        </div>

                        {/*  ========= Button Container ========= */}
                        <div className='flex flex-row gap-2 px-4 py-3'>

                            <div className='flex flex-row gap-2'>

                                {/* Submit */}
                                <button
                                    className='Primary-Button whitespace-nowrap'
                                    type='submit'
                                    value='submit'
                                    disabled={duplicateSubnet === 'complete' ? false : true}
                                >
                                    Submit
                                </button>

                                {/* Cancel */}
                                <button className='Secondary-Button whitespace-nowrap' onClick={() => {

                                    // close side pane
                                    reset();
                                    setDuplicateSubnet('onload');
                                    setAddSubnet(false);

                                }}>Cancel</button>

                            </div>

                        </div>

                    </form>

                </dialog>
            </div>
        </>
    )
}
